import { useState, useEffect, useRef } from "react";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy: "#0a0f1e",
  navyMid: "#0d1526",
  navyLight: "#111d35",
  navyBorder: "#1a2a4a",
  navySurface: "#152040",
  slate: "#1e2d4a",
  slateMid: "#243559",
  amber: "#f59e0b",
  amberDim: "#d97706",
  amberGlow: "rgba(245,158,11,0.15)",
  teal: "#0ea5e9",
  tealDim: "#0284c7",
  green: "#10b981",
  greenDim: "#059669",
  red: "#ef4444",
  redDim: "#dc2626",
  purple: "#8b5cf6",
  textPrimary: "#e8f0fe",
  textSecondary: "#8ba3c7",
  textMuted: "#4d6a94",
  textDim: "#2d4a6e",
  mono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  sans: "'DM Sans', 'Outfit', system-ui, sans-serif",
  display: "'DM Serif Display', 'Playfair Display', Georgia, serif",
};

// ─── MOCK DATA ─────────────────────────────────────────────────────────────────
const MOCK_SITES = [
  { id: "s1", name: "Jefferson Dental Lakeland", status: "live", calls_today: 34, bookings_today: 12, agent_version: "2.1", denticon_status: "synced", template: "Standard Receptionist v2", timezone: "America/New_York", phone: "+18635550100", last_deploy: "2h ago" },
  { id: "s2", name: "Sunshine Dental Tampa", status: "live", calls_today: 28, bookings_today: 9, agent_version: "2.1", denticon_status: "synced", template: "Standard Receptionist v2", timezone: "America/New_York", phone: "+18135550200", last_deploy: "2h ago" },
  { id: "s3", name: "Gulf Coast Smiles Sarasota", status: "live", calls_today: 19, bookings_today: 7, agent_version: "2.0", denticon_status: "synced", template: "Standard Receptionist v2", timezone: "America/New_York", phone: "+19415550300", last_deploy: "3d ago" },
  { id: "s4", name: "Orlando Family Dentistry", status: "deploying", calls_today: 0, bookings_today: 0, agent_version: "—", denticon_status: "pending", template: "Standard Receptionist v2", timezone: "America/New_York", phone: "+14075550400", last_deploy: "—" },
  { id: "s5", name: "Miami Beach Dental", status: "error", calls_today: 0, bookings_today: 0, agent_version: "1.9", denticon_status: "error", template: "Premium Receptionist v1", timezone: "America/New_York", phone: "+13055550500", last_deploy: "5d ago" },
  { id: "s6", name: "Clearwater Dental Arts", status: "live", calls_today: 41, bookings_today: 15, agent_version: "2.1", denticon_status: "synced", template: "Standard Receptionist v2", timezone: "America/New_York", phone: "+17275550600", last_deploy: "2h ago" },
];


const MOCK_CALLS = [
  { id: "c1", site: "Jefferson Dental Lakeland", patient: "Sarah M.", duration: "4:22", status: "completed", outcome: "booked", time: "9:14 AM", conf: "APPT-4421", agent_ver: "2.1" },
  { id: "c2", site: "Clearwater Dental Arts", patient: "James R.", duration: "2:08", status: "completed", outcome: "info_only", time: "9:31 AM", conf: null, agent_ver: "2.1" },
  { id: "c3", site: "Sunshine Dental Tampa", patient: "Maria G.", duration: "6:44", status: "escalated", outcome: "transferred", time: "9:47 AM", conf: null, agent_ver: "2.1" },
  { id: "c4", site: "Jefferson Dental Lakeland", patient: "Tom W.", duration: "3:15", status: "completed", outcome: "booked", time: "10:02 AM", conf: "APPT-4422", agent_ver: "2.1" },
  { id: "c5", site: "Gulf Coast Smiles Sarasota", patient: "Linda K.", duration: "1:55", status: "completed", outcome: "cancelled", time: "10:18 AM", conf: null, agent_ver: "2.0" },
  { id: "c6", site: "Clearwater Dental Arts", patient: "Robert P.", duration: "5:02", status: "completed", outcome: "booked", time: "10:33 AM", conf: "APPT-4423", agent_ver: "2.1" },
  { id: "c7", site: "Jefferson Dental Lakeland", patient: "Anna S.", duration: "8:11", status: "escalated", outcome: "transferred", time: "10:51 AM", conf: null, agent_ver: "2.1" },
  { id: "c8", site: "Sunshine Dental Tampa", patient: "Carlos D.", duration: "3:44", status: "completed", outcome: "booked", time: "11:09 AM", conf: "APPT-4424", agent_ver: "2.1" },
];


// ─── REAL API CONFIG ──────────────────────────────────────────────────────────
const API_BASE = "https://jdoaihub.com/dv-api";
const API_KEY  = "Ho9TjNOCa2W48U5Zw0t1Xr3p58gXPSdi";
const HEADERS  = { "Authorization": `Bearer ${API_KEY}`, "Content-Type": "application/json" };

async function fetchLiveCalls() {
  try {
    const r    = await fetch(`${API_BASE}/calls`, { headers: HEADERS });
    const data = await r.json();
    return data.calls || [];
  } catch {
    return [];
  }
}

async function fetchLiveSites() {
  try {
    const r    = await fetch(`${API_BASE}/sites`, { headers: HEADERS });
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

async function fetchLiveStats() {
  try {
    const r = await fetch(`${API_BASE}/stats`, { headers: HEADERS });
    return await r.json();
  } catch {
    return null;
  }
}

async function createSite(siteData) {
  const r = await fetch(`${API_BASE}/sites`, {
    method: "POST", headers: HEADERS, body: JSON.stringify(siteData),
  });
  return r.json();
}

async function fetchLiveTemplates() {
  try {
    const r    = await fetch(`${API_BASE}/templates`, { headers: HEADERS });
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

async function saveTemplate(id, updates) {
  const r = await fetch(`${API_BASE}/templates/${id}`, {
    method: "PUT", headers: HEADERS, body: JSON.stringify(updates),
  });
  return r.json();
}

async function createTemplate(data) {
  const r = await fetch(`${API_BASE}/templates`, {
    method: "POST", headers: HEADERS, body: JSON.stringify(data),
  });
  return r.json();
}

async function fetchLiveCallbacks() {
  try {
    const r    = await fetch(`${API_BASE}/callbacks`, { headers: HEADERS });
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}


const MOCK_TEMPLATES = [
  { id: "t1", name: "Standard Receptionist v2", sites: 5, version: "2.1", published: true, updated: "3 days ago", tools: 7, flows: 8 },
  { id: "t2", name: "Premium Receptionist v1", sites: 1, version: "1.0", published: true, updated: "2 weeks ago", tools: 7, flows: 10 },
  { id: "t3", name: "Ortho Specialist Draft", sites: 0, version: "0.3", published: false, updated: "1 day ago", tools: 6, flows: 6 },
];

const HOUR_LABELS = ["8a","9a","10a","11a","12p","1p","2p","3p","4p","5p"];
const CALL_VOLUMES = [4,11,18,22,15,19,24,21,13,7];
const BOOKING_RATES = [75,82,67,91,73,88,85,79,82,71];

// ─── UTILITIES ─────────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const colors = {
    live: T.green, deploying: T.amber, error: T.red,
    synced: T.green, pending: T.amber, draft: T.textMuted,
    completed: T.green, escalated: T.amber, failed: T.red,
  };
  const pulse = status === "live" || status === "deploying";
  return (
    <span style={{ position: "relative", display: "inline-block", width: 8, height: 8 }}>
      <span style={{
        display: "block", width: 8, height: 8, borderRadius: "50%",
        background: colors[status] || T.textMuted,
      }} />
      {pulse && <span style={{
        position: "absolute", inset: -2, borderRadius: "50%",
        border: `1.5px solid ${colors[status]}`,
        animation: "pulse-ring 2s ease-out infinite",
      }} />}
    </span>
  );
}

function Badge({ children, color = T.teal }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 8px", borderRadius: 4,
      fontSize: 11, fontFamily: T.mono, fontWeight: 600, letterSpacing: "0.04em",
      background: color + "22", color: color,
      border: `1px solid ${color}44`,
    }}>{children}</span>
  );
}

function Metric({ label, value, sub, color = T.teal, large }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: large ? 32 : 22, fontFamily: T.mono, fontWeight: 700, color, letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, fontFamily: T.mono, color: color + "99" }}>{sub}</div>}
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Card({ children, style = {}, glow }) {
  return (
    <div style={{
      background: T.navyMid,
      border: `1px solid ${glow ? T.amber + "55" : T.navyBorder}`,
      borderRadius: 10,
      padding: "20px 22px",
      boxShadow: glow ? `0 0 24px ${T.amberGlow}` : "none",
      ...style,
    }}>{children}</div>
  );
}

function SectionHead({ children }) {
  return (
    <div style={{
      fontSize: 11, fontFamily: T.mono, fontWeight: 700, letterSpacing: "0.12em",
      color: T.textMuted, textTransform: "uppercase", marginBottom: 14,
      borderBottom: `1px solid ${T.navyBorder}`, paddingBottom: 8,
    }}>{children}</div>
  );
}

function PushButton({ onClick, loading, label = "Push Config", site }) {
  const [state, setState] = useState("idle");
  const handleClick = async () => {
    setState("loading");
    await new Promise(r => setTimeout(r, 2200));
    setState("done");
    setTimeout(() => setState("idle"), 3000);
    if (onClick) onClick();
  };
  const bg = state === "done" ? T.green : T.amber;
  return (
    <button onClick={handleClick} style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "8px 16px", borderRadius: 6, border: "none", cursor: "pointer",
      background: state === "done" ? T.green + "22" : T.amberGlow,
      color: bg, fontSize: 12, fontFamily: T.mono, fontWeight: 700,
      border: `1px solid ${bg}55`, transition: "all 0.2s",
    }}>
      {state === "loading" && <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span>}
      {state === "done" ? "✓ Deployed" : state === "loading" ? "Deploying..." : label}
    </button>
  );
}

// ─── MINI CHART ────────────────────────────────────────────────────────────────
function SparkBar({ data, color = T.teal, height = 40 }) {
  const max = Math.max(...data);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height }}>
      {data.map((v, i) => (
        <div key={i} style={{
          flex: 1, borderRadius: "2px 2px 0 0",
          background: color + (i === data.length - 1 ? "ff" : "77"),
          height: `${(v / max) * 100}%`, minHeight: 2,
          transition: "height 0.3s ease",
        }} />
      ))}
    </div>
  );
}

function HourlyChart({ data, labels, color = T.teal, height = 80 }) {
  const max = Math.max(...data);
  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height }}>
        {data.map((v, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{
              width: "100%", borderRadius: "3px 3px 0 0",
              background: `linear-gradient(to top, ${color}, ${color}99)`,
              height: `${(v / max) * (height - 20)}px`, minHeight: 2,
            }} />
            <span style={{ fontSize: 9, fontFamily: T.mono, color: T.textMuted }}>{labels[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SIDEBAR NAV ───────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: "dashboard",  label: "Dashboard",  icon: "◈" },
  { id: "sites",      label: "Sites",      icon: "◉" },
  { id: "templates",  label: "Templates",  icon: "◫" },
  { id: "calls",      label: "Call Logs",  icon: "◎" },
  { id: "callbacks",  label: "Callbacks",  icon: "↩" },
  { id: "analytics",  label: "Analytics",  icon: "◑" },
  { id: "compliance", label: "Compliance", icon: "◻" },
  { id: "billing",    label: "Billing",    icon: "◈" },
  { id: "settings",   label: "Settings",   icon: "◧" },
];

function Sidebar({ active, onNav, callCount = 0, user, onLogout }) {
  return (
    <div style={{
      width: 220, background: T.navy, borderRight: `1px solid ${T.navyBorder}`,
      display: "flex", flexDirection: "column", flexShrink: 0,
      position: "sticky", top: 0, height: "100vh",
    }}>
      {/* Logo */}
      <div style={{ padding: "22px 20px 18px", borderBottom: `1px solid ${T.navyBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `linear-gradient(135deg, ${T.amber}, ${T.amberDim})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, color: T.navy, fontWeight: 900,
          }}>D</div>
          <div>
            <div style={{ fontSize: 15, fontFamily: T.sans, fontWeight: 700, color: T.textPrimary, letterSpacing: "-0.01em" }}>DentaVoice</div>
            <div style={{ fontSize: 10, fontFamily: T.mono, color: T.amber, letterSpacing: "0.08em" }}>DSO PLATFORM</div>
          </div>
        </div>
      </div>

      {/* Org selector */}
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.navyBorder}` }}>
        <div style={{
          padding: "8px 12px", borderRadius: 6, background: T.navyLight,
          border: `1px solid ${T.navyBorder}`, cursor: "pointer",
        }}>
          <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 2 }}>ORGANIZATION</div>
          <div style={{ fontSize: 13, color: T.textPrimary, fontFamily: T.sans, fontWeight: 600 }}>Jefferson Dental</div>
          <div style={{ fontSize: 10, fontFamily: T.mono, color: T.textMuted, marginTop: 1 }}>DSO Platform ▾</div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
        {NAV_ITEMS.map(item => (
          <button key={item.id} onClick={() => onNav(item.id)} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "9px 12px", borderRadius: 6, border: "none", cursor: "pointer",
            background: active === item.id ? T.slateMid : "transparent",
            color: active === item.id ? T.textPrimary : T.textSecondary,
            fontSize: 13, fontFamily: T.sans, fontWeight: active === item.id ? 600 : 400,
            textAlign: "left", transition: "all 0.15s",
            borderLeft: active === item.id ? `2px solid ${T.amber}` : "2px solid transparent",
          }}>
            <span style={{ fontSize: 14, opacity: 0.8 }}>{item.icon}</span>
            {item.label}
            {item.id === "calls" && callCount > 0 && <span style={{
              marginLeft: "auto", fontSize: 10, fontFamily: T.mono,
              background: T.amber + "22", color: T.amber,
              padding: "1px 6px", borderRadius: 10,
            }}>{callCount}</span>}
          </button>
        ))}
      </nav>

      {/* Bottom user */}
      <div style={{ padding: "14px 16px", borderTop: `1px solid ${T.navyBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: "50%",
            background: `linear-gradient(135deg, ${T.teal}, ${T.purple})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0,
          }}>{user?.name ? user.name.charAt(0).toUpperCase() : "U"}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontFamily: T.sans, fontWeight: 600, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name || "User"}</div>
            <div style={{ fontSize: 10, fontFamily: T.mono, color: T.textMuted }}>{user?.role || "admin"}</div>
          </div>
          <button onClick={onLogout} title="Sign out" style={{
            background: "none", border: "none", cursor: "pointer",
            color: T.textMuted, fontSize: 14, padding: 4,
            borderRadius: 4, flexShrink: 0,
          }} onMouseEnter={e => e.target.style.color = T.red}
             onMouseLeave={e => e.target.style.color = T.textMuted}>
            ⏻
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TOPBAR ────────────────────────────────────────────────────────────────────
function TopBar({ title, sub, children }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div style={{
      height: 56, background: T.navy,
      borderBottom: `1px solid ${T.navyBorder}`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 24px", flexShrink: 0, position: "sticky", top: 0, zIndex: 10,
    }}>
      <div>
        <div style={{ fontSize: 16, fontFamily: T.sans, fontWeight: 700, color: T.textPrimary }}>{title}</div>
        {sub && <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>{sub}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {children}
        <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>
          {time.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <StatusDot status="live" />
          <span style={{ fontSize: 11, fontFamily: T.mono, color: T.green }}>SYSTEM NOMINAL</span>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD PAGE ────────────────────────────────────────────────────────────
function DashboardPage({ onNav }) {
  const [recentCalls, setRecentCalls] = useState([]);
  const [liveSites, setLiveSites]     = useState([]);
  const [stats, setStats]             = useState(null);
  const [callbacks, setCallbacks]     = useState([]);

  useEffect(() => {
    fetchLiveCalls().then(setRecentCalls);
    fetchLiveSites().then(setLiveSites);
    fetchLiveStats().then(setStats);
    fetchLiveCallbacks().then(setCallbacks);
    const t1 = setInterval(() => fetchLiveCalls().then(setRecentCalls), 30000);
    const t2 = setInterval(() => fetchLiveSites().then(setLiveSites), 60000);
    const t3 = setInterval(() => fetchLiveStats().then(setStats), 30000);
    const t4 = setInterval(() => fetchLiveCallbacks().then(setCallbacks), 60000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); clearInterval(t4); };
  }, []);

  // Compute hourly breakdown from real calls
  const hourlyData = Array(10).fill(0); // 8am–5pm
  const hourlyBooked = Array(10).fill(0);
  recentCalls.forEach(call => {
    const t = call.call_time || call.time || "";
    const match = t.match(/(\d+):/);
    if (!match) return;
    let h = parseInt(match[1]);
    if (t.toLowerCase().includes("pm") && h !== 12) h += 12;
    if (t.toLowerCase().includes("am") && h === 12) h = 0;
    const idx = h - 8;
    if (idx >= 0 && idx < 10) {
      hourlyData[idx]++;
      if (call.outcome === "booked") hourlyBooked[idx]++;
    }
  });

  // Last booked call for ticker
  const lastBooked = recentCalls.find(c => c.outcome === "booked");
  const liveSiteCount = liveSites.filter(s => s.status === "live").length;
  const todayEsc = stats?.today?.escalated ?? 0;

  return (
    <div>
      <TopBar title="Operations Dashboard" sub="REAL-TIME OVERVIEW" />
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>

        {/* Live ticker — all real */}
        <div style={{
          background: T.navyLight, border: `1px solid ${T.amber}44`,
          borderRadius: 8, padding: "10px 16px",
          display: "flex", alignItems: "center", gap: 20,
          overflow: "hidden",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <StatusDot status="live" />
            <span style={{ fontSize: 11, fontFamily: T.mono, fontWeight: 700, color: T.amber, letterSpacing: "0.1em" }}>LIVE</span>
          </div>
          {[
            stats ? `${stats.today?.total_calls ?? 0} calls today` : "Loading...",
            lastBooked
              ? `Last booking: ${lastBooked.conf || lastBooked.confirmation_id || "—"} · ${lastBooked.site_name || lastBooked.site || "—"} · ${lastBooked.call_time || lastBooked.time || ""}`
              : "No bookings yet today",
            `${liveSiteCount} of ${liveSites.length} sites live`,
            `${todayEsc} escalation${todayEsc !== 1 ? "s" : ""} today`,
          ].map((item, i) => (
            <span key={i} style={{ fontSize: 12, fontFamily: T.mono, color: T.textSecondary }}>
              <span style={{ color: T.textDim, marginRight: 12 }}>·</span>{item}
            </span>
          ))}
        </div>

        {/* Key metrics — all real from /stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {[
            {
              label: "Total calls today",
              value: stats ? String(stats.today?.total_calls ?? 0) : "—",
              sub: stats ? `${stats.month?.total_calls ?? 0} this month` : "Loading...",
              color: T.teal,
            },
            {
              label: "Appointments booked",
              value: stats ? String(stats.today?.booked ?? 0) : "—",
              sub: stats ? `${stats.today?.booking_rate ?? 0}% conversion` : "Loading...",
              color: T.green,
            },
            {
              label: "Escalations today",
              value: stats ? String(stats.today?.escalated ?? 0) : "—",
              sub: stats && stats.today?.total_calls > 0
                ? `${Math.round((stats.today.escalated / stats.today.total_calls) * 100)}% rate`
                : "No calls yet",
              color: T.amber,
            },
            {
              label: "Active sites",
              value: String(liveSiteCount),
              sub: `${liveSites.length} total configured`,
              color: T.green,
            },
          ].map((m, i) => (
            <Card key={i}>
              <Metric {...m} large />
              <SparkBar
                data={hourlyData.length > 0 && hourlyData.some(v => v > 0)
                  ? hourlyData
                  : [1,1,1,1,1,1,1,1,1,1]}
                color={m.color} height={32}
              />
            </Card>
          ))}
        </div>

        {/* Sites + Charts */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 14 }}>
          <Card>
            <SectionHead>Live site status</SectionHead>
            {liveSites.length === 0 ? (
              <div style={{ color: T.textMuted, fontFamily: T.mono, fontSize: 13, padding: "20px 0", textAlign: "center" }}>
                No sites configured yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {liveSites.map(site => (
                  <div key={site.id} onClick={() => onNav("sites")} style={{
                    display: "grid", gridTemplateColumns: "1fr 80px 80px 100px auto",
                    alignItems: "center", gap: 12,
                    padding: "10px 12px", borderRadius: 6, cursor: "pointer",
                    background: "transparent", transition: "background 0.15s",
                    borderBottom: `1px solid ${T.navyBorder}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = T.navyLight}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <StatusDot status={site.status} />
                      <span style={{ fontSize: 13, fontFamily: T.sans, color: T.textPrimary, fontWeight: 500 }}>{site.name}</span>
                    </div>
                    <div style={{ fontFamily: T.mono, fontSize: 13, color: T.teal, textAlign: "right" }}>{site.calls_today ?? 0} calls</div>
                    <div style={{ fontFamily: T.mono, fontSize: 13, color: T.green, textAlign: "right" }}>{site.bookings_today ?? 0} booked</div>
                    <Badge color={site.denticon_status === "synced" ? T.green : site.denticon_status === "pending" ? T.amber : T.red}>
                      {site.denticon_status === "synced" ? "DENTICON ✓" : site.denticon_status === "pending" ? "PENDING" : "DENTICON ERR"}
                    </Badge>
                    <Badge color={T.textMuted}>v{site.agent_version || "—"}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card style={{ flex: 1 }}>
              <SectionHead>Calls by hour (today)</SectionHead>
              <HourlyChart
                data={hourlyData.some(v => v > 0) ? hourlyData : CALL_VOLUMES}
                labels={HOUR_LABELS} color={T.teal} height={90}
              />
            </Card>
            <Card style={{ flex: 1 }}>
              <SectionHead>Bookings by hour (today)</SectionHead>
              <HourlyChart
                data={hourlyBooked.some(v => v > 0) ? hourlyBooked : BOOKING_RATES.map(v => Math.round(v / 10))}
                labels={HOUR_LABELS} color={T.green} height={90}
              />
            </Card>
          </div>
        </div>

        {/* Recent calls — all real */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <SectionHead>Recent calls</SectionHead>
            <button onClick={() => onNav("calls")} style={{
              fontSize: 11, fontFamily: T.mono, color: T.teal,
              background: "none", border: "none", cursor: "pointer",
            }}>View all →</button>
          </div>
          {recentCalls.length === 0 ? (
            <div style={{ color: T.textMuted, fontFamily: T.mono, fontSize: 13, padding: "20px 0", textAlign: "center" }}>
              No calls yet — make a test call to your Twilio number.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {recentCalls.slice(0, 5).map(call => (
                <div key={call.id} style={{
                  display: "grid", gridTemplateColumns: "70px 1fr 160px 80px 120px 120px",
                  alignItems: "center", gap: 12, padding: "9px 10px",
                  borderBottom: `1px solid ${T.navyBorder}`,
                  fontSize: 12,
                }}>
                  <span style={{ fontFamily: T.mono, color: T.textMuted }}>{call.call_time || call.time || "—"}</span>
                  <span style={{ color: T.textPrimary, fontFamily: T.sans }}>{call.patient || "Unknown" || "Unknown"}</span>
                  <span style={{ color: T.textSecondary, fontSize: 11 }}>{call.site_name || call.site || "—"}</span>
                  <span style={{ fontFamily: T.mono, color: T.textMuted }}>{call.duration || "—"}</span>
                  <Badge color={call.outcome === "booked" ? T.green : call.outcome === "transferred" ? T.amber : T.textMuted}>
                    {(call.outcome || "unknown").toUpperCase()}
                  </Badge>
                  {call.conf || call.confirmation_id
                    ? <span style={{ fontFamily: T.mono, fontSize: 11, color: T.teal }}>{call.conf || call.confirmation_id}</span>
                    : <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textDim }}>—</span>
                  }
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Callbacks widget */}
        {callbacks.length > 0 && (
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <SectionHead>Missed call recovery</SectionHead>
              <button onClick={() => onNav("callbacks")} style={{ fontSize: 11, fontFamily: T.mono, color: T.teal, background: "none", border: "none", cursor: "pointer" }}>View all →</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {callbacks.slice(0, 5).map(cb => (
                <div key={cb.id} style={{
                  display: "grid", gridTemplateColumns: "1fr 140px 100px 90px",
                  gap: 12, padding: "10px 12px", fontSize: 12,
                  borderBottom: `1px solid ${T.navyBorder}`, alignItems: "center",
                }}>
                  <span style={{ color: T.textPrimary, fontFamily: T.sans }}>{cb.patient_name || "Unknown"}</span>
                  <span style={{ color: T.textSecondary, fontSize: 11 }}>{cb.site_name || "—"}</span>
                  <span style={{ fontFamily: T.mono, color: T.textMuted, fontSize: 11 }}>{cb.from_number || "—"}</span>
                  <Badge color={
                    cb.status === "booked"  ? T.green  :
                    cb.status === "called"  ? T.teal   :
                    cb.status === "pending" ? T.amber  :
                    cb.status === "error"   ? T.red    : T.textMuted
                  }>{(cb.status || "pending").toUpperCase()}</Badge>
                </div>
              ))}
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}
function SitesPage() {
  const [selectedSite, setSelectedSite] = useState(null);
  const [activeTab, setActiveTab]       = useState("overview");
  const [showNew, setShowNew]           = useState(false);
  const [sites, setSites]               = useState([]);
  const [liveCalls, setLiveCalls]       = useState([]);

  useEffect(() => {
    fetchLiveSites().then(setSites);
    fetchLiveCalls().then(setLiveCalls);
    const t1 = setInterval(() => fetchLiveSites().then(setSites), 60000);
    const t2 = setInterval(() => fetchLiveCalls().then(setLiveCalls), 30000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  const site = selectedSite ? sites.find(s => s.id === selectedSite) : null;

  if (showNew) return <NewSiteWizard onBack={() => { setShowNew(false); fetchLiveSites().then(setSites); }} />;

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* Site list */}
      <div style={{
        width: 320, background: T.navy, borderRight: `1px solid ${T.navyBorder}`,
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{
          padding: "14px 16px", borderBottom: `1px solid ${T.navyBorder}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ fontSize: 13, fontFamily: T.sans, fontWeight: 700, color: T.textPrimary }}>
            Sites <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textMuted }}>({sites.length})</span>
          </div>
          <button onClick={() => setShowNew(true)} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.amber}55`,
            background: T.amberGlow, color: T.amber, cursor: "pointer",
            fontSize: 12, fontFamily: T.mono, fontWeight: 700,
          }}>+ New Site</button>
        </div>
        <div style={{ overflow: "auto", flex: 1 }}>
          {sites.map(s => (
            <div key={s.id} onClick={() => setSelectedSite(s.id)} style={{
              padding: "14px 16px",
              borderBottom: `1px solid ${T.navyBorder}`,
              cursor: "pointer",
              background: selectedSite === s.id ? T.navyLight : "transparent",
              borderLeft: selectedSite === s.id ? `2px solid ${T.amber}` : "2px solid transparent",
              transition: "all 0.15s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <StatusDot status={s.status} />
                <span style={{ fontSize: 13, fontFamily: T.sans, fontWeight: 600, color: T.textPrimary }}>{s.name}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <Badge color={s.denticon_status === "synced" ? T.green : T.red}>
                  {s.denticon_status === "synced" ? "SYNC ✓" : "SYNC ERR"}
                </Badge>
                <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>{s.calls_today} calls today</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Site detail */}
      {site ? (
        <div style={{ flex: 1, overflow: "auto" }}>
          <TopBar title={site.name} sub={`${site.phone} · ${site.timezone} · Template: ${site.template}`}>
            <PushButton label="↑ Push Config" />
          </TopBar>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.navyBorder}`, background: T.navy, padding: "0 24px" }}>
            {["overview","denticon","agent","calls"].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                padding: "12px 16px", border: "none", background: "none", cursor: "pointer",
                fontSize: 12, fontFamily: T.mono, fontWeight: 600,
                color: activeTab === tab ? T.amber : T.textSecondary,
                borderBottom: activeTab === tab ? `2px solid ${T.amber}` : "2px solid transparent",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>{tab}</button>
            ))}
          </div>

          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 18 }}>
            {activeTab === "overview" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                  {[
                    { label: "Calls today", value: site.calls_today, color: T.teal },
                    { label: "Bookings today", value: site.bookings_today, color: T.green },
                    { label: "Agent version", value: `v${site.agent_version}`, color: T.textSecondary },
                    { label: "Last deploy", value: site.last_deploy, color: T.textMuted },
                  ].map((m, i) => <Card key={i}><Metric {...m} /></Card>)}
                </div>
                <Card>
                  <SectionHead>Site configuration</SectionHead>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    {[
                      ["Phone number", site.phone],
                      ["Timezone", site.timezone],
                      ["Template", site.template],
                      ["Status", site.status],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 4 }}>{k.toUpperCase()}</div>
                        <div style={{ fontSize: 13, fontFamily: k === "Phone number" ? T.mono : T.sans, color: T.textPrimary }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            )}
            {activeTab === "denticon" && <DenticonConfigPanel site={site} />}
            {activeTab === "agent" && <AgentConfigPanel site={site} />}
            {activeTab === "calls" && (
              <Card>
                <SectionHead>Recent calls at this site</SectionHead>
                {liveCalls.filter(c => c.site_name === site.name || c.site === site.name || c.site_name === site.name).map(call => (
                  <div key={call.id} style={{
                    display: "flex", alignItems: "center", gap: 14, padding: "10px 0",
                    borderBottom: `1px solid ${T.navyBorder}`, fontSize: 12,
                  }}>
                    <span style={{ fontFamily: T.mono, color: T.textMuted, width: 60 }}>{call.call_time || call.time || "—"}</span>
                    <span style={{ color: T.textPrimary, flex: 1 }}>{call.patient || "Unknown"}</span>
                    <span style={{ fontFamily: T.mono, color: T.textMuted }}>{call.duration}</span>
                    <Badge color={call.outcome === "booked" ? T.green : call.outcome === "transferred" ? T.amber : T.textMuted}>
                      {call.outcome.toUpperCase()}
                    </Badge>
                  </div>
                ))}
                {liveCalls.filter(c => c.site_name === site.name || c.site === site.name || c.site_name === site.name).length === 0 && (
                  <div style={{ color: T.textMuted, fontSize: 13, padding: "20px 0", textAlign: "center" }}>No recent calls at this site.</div>
                )}
              </Card>
            )}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted, fontFamily: T.mono, fontSize: 13 }}>
          Select a site to view details
        </div>
      )}
    </div>
  );
}

function DenticonConfigPanel({ site }) {
  const [tested, setTested] = useState(null);
  const [testing, setTesting] = useState(false);
  const test = async () => {
    setTesting(true);
    await new Promise(r => setTimeout(r, 1800));
    setTesting(false);
    setTested(site.denticon_status === "synced" ? "ok" : "err");
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card glow={site.denticon_status !== "synced"}>
        <SectionHead>Denticon credentials</SectionHead>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          {["PGID", "OID", "API Key", "Base URL"].map(field => (
            <div key={field}>
              <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 6 }}>{field}</div>
              <div style={{
                padding: "8px 12px", borderRadius: 6, background: T.navyLight,
                border: `1px solid ${T.navyBorder}`, fontSize: 12, fontFamily: T.mono,
                color: field === "API Key" ? T.textMuted : T.textPrimary,
              }}>
                {field === "API Key" ? "••••••••••••••••" : field === "PGID" ? "PG-4421" : field === "OID" ? "OFF-4421" : "https://api.planetdds.com/v2"}
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 10 }}>
          Credentials stored in Azure Key Vault — never displayed in plaintext. Reference: kv-dentavoice-prod.vault.azure.net
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={test} style={{
            padding: "8px 16px", borderRadius: 6, cursor: "pointer",
            background: T.navySurface, border: `1px solid ${T.navyBorder}`,
            color: T.textSecondary, fontSize: 12, fontFamily: T.mono,
          }}>
            {testing ? "Testing…" : "Test Connection"}
          </button>
          {tested === "ok" && <Badge color={T.green}>CONNECTION OK</Badge>}
          {tested === "err" && <Badge color={T.red}>CONNECTION FAILED</Badge>}
        </div>
      </Card>
      <Card>
        <SectionHead>Sync status</SectionHead>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
          {[
            { label: "Providers synced", value: site.denticon_status === "synced" ? "4" : "0" },
            { label: "Last sync", value: site.denticon_status === "synced" ? "2 min ago" : "Failed" },
            { label: "Writebacks (24h)", value: site.denticon_status === "synced" ? "43" : "0" },
          ].map((m, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 4 }}>{m.label}</div>
              <div style={{ fontSize: 18, fontFamily: T.mono, color: T.teal }}>{m.value}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function AgentConfigPanel({ site }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card>
        <SectionHead>Deployed agent</SectionHead>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
          {[
            ["Retell agent ID", "ret_ag_abc123def456"],
            ["Config version", `v${site.agent_version}`],
            ["Deploy status", site.status],
            ["Template", site.template],
            ["LLM model", "gpt-4o"],
            ["Voice", "Sarah Professional"],
          ].map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 4 }}>{k}</div>
              <div style={{ fontSize: 12, fontFamily: k === "Retell agent ID" ? T.mono : T.sans, color: T.textPrimary }}>{v}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <SectionHead>Custom prompt overrides</SectionHead>
        <div style={{
          background: T.navy, border: `1px solid ${T.navyBorder}`,
          borderRadius: 6, padding: 14, fontFamily: T.mono, fontSize: 11,
          color: T.textSecondary, lineHeight: 1.7,
          maxHeight: 180, overflow: "auto",
        }}>
{`// Site-specific suffix injected into system prompt
site_name: "Jefferson Dental Lakeland"
address: "123 Main Street, Lakeland, FL 33801"
hours: "Mon–Fri 8 AM – 5 PM, Sat 9 AM – 1 PM"
providers: "Dr. Patel and Dr. Kim"
insurance: "Delta Dental, Cigna, Aetna, BlueCross, Humana"
custom_suffix: "We offer same-day emergency appointments
  and free new patient consultations."`}
        </div>
      </Card>
      <div style={{ display: "flex", gap: 10 }}>
        <PushButton label="↑ Push Config to Retell" />
        <button style={{
          padding: "8px 16px", borderRadius: 6, cursor: "pointer",
          background: T.navySurface, border: `1px solid ${T.navyBorder}`,
          color: T.textSecondary, fontSize: 12, fontFamily: T.mono,
        }}>Run Test Call</button>
        <button style={{
          padding: "8px 16px", borderRadius: 6, cursor: "pointer",
          background: T.navySurface, border: `1px solid ${T.navyBorder}`,
          color: T.textSecondary, fontSize: 12, fontFamily: T.mono,
        }}>Rollback v2.0</button>
      </div>
    </div>
  );
}

// ─── NEW SITE WIZARD ───────────────────────────────────────────────────────────
function NewSiteWizard({ onBack }) {
  const [step, setStep]       = useState(0);
  const [saving, setSaving]   = useState(false);
  const [templates, setTemplates] = useState([]);
  const [form, setForm]       = useState({ name: "", phone: "", timezone: "America/New_York", template: "", pgid: "", oid: "", apikey: "" });
  const steps = ["Basic Info", "Denticon Config", "Agent Deploy", "Complete"];

  useEffect(() => {
    fetchLiveTemplates().then(data => {
      setTemplates(data);
      if (data.length > 0) setForm(f => ({ ...f, template: data[0].id }));
    });
  }, []);

  const handleDeploy = async () => {
    setSaving(true);
    try {
      await createSite({
        name:            form.name,
        phone:           form.phone,
        timezone:        form.timezone,
        template:        templates.find(t => t.id === form.template)?.name || form.template,
        status:          "live",
        denticon_status: form.pgid ? "synced" : "pending",
        agent_version:   "2.1",
        last_deploy:     new Date().toLocaleTimeString(),
      });
    } catch (e) {
      console.error("Site creation failed:", e);
    }
    setSaving(false);
    setStep(3);
  };

  return (
    <div>
      <TopBar title="New Site Setup" sub="ONBOARDING WIZARD">
        <button onClick={onBack} style={{ fontSize: 12, fontFamily: T.mono, color: T.textMuted, background: "none", border: "none", cursor: "pointer" }}>← Back</button>
      </TopBar>
      <div style={{ padding: 32, maxWidth: 640, margin: "0 auto" }}>
        {/* Stepper */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 32, gap: 0 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: i < step ? T.green : i === step ? T.amber : T.navyBorder,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontFamily: T.mono, fontWeight: 700,
                  color: i <= step ? T.navy : T.textMuted,
                }}>{i < step ? "✓" : i + 1}</div>
                <span style={{ fontSize: 12, fontFamily: T.sans, color: i === step ? T.textPrimary : T.textMuted, fontWeight: i === step ? 600 : 400 }}>{s}</span>
              </div>
              {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: i < step ? T.green + "66" : T.navyBorder, margin: "0 12px" }} />}
            </div>
          ))}
        </div>

        <Card>
          {step === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <SectionHead>Basic site information</SectionHead>
              {[
                ["Site name", "name", "Jefferson Dental Lakeland", "text"],
                ["Twilio phone number", "phone", "+1 (863) 555-0100", "tel"],
              ].map(([label, key, placeholder, type]) => (
                <div key={key}>
                  <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 6 }}>{label.toUpperCase()}</div>
                  <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder} type={type}
                    style={{
                      width: "100%", padding: "10px 14px", borderRadius: 6,
                      background: T.navyLight, border: `1px solid ${T.navyBorder}`,
                      color: T.textPrimary, fontSize: 13, fontFamily: T.sans,
                      outline: "none", boxSizing: "border-box",
                    }} />
                </div>
              ))}
              <div>
                <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 6 }}>TEMPLATE</div>
                <select value={form.template} onChange={e => setForm(f => ({ ...f, template: e.target.value }))} style={{
                  width: "100%", padding: "10px 14px", borderRadius: 6,
                  background: T.navyLight, border: `1px solid ${T.navyBorder}`,
                  color: T.textPrimary, fontSize: 13, fontFamily: T.sans,
                }}>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name} (v{t.version})</option>)}
                </select>
              </div>
            </div>
          )}
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <SectionHead>Denticon credentials</SectionHead>
              <div style={{ padding: 12, background: T.amberGlow, border: `1px solid ${T.amber}44`, borderRadius: 6, fontSize: 11, fontFamily: T.mono, color: T.amber }}>
                Credentials will be stored in Azure Key Vault. They will never appear in logs or the database.
              </div>
              {[["PGID", "pgid", "PG-4421"], ["OID (Office ID)", "oid", "OFF-4421"], ["API Key", "apikey", "••••••••••••••••"]].map(([label, key, ph]) => (
                <div key={key}>
                  <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 6 }}>{label.toUpperCase()}</div>
                  <input type={key === "apikey" ? "password" : "text"} placeholder={ph}
                    value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: 6, background: T.navyLight, border: `1px solid ${T.navyBorder}`, color: T.textPrimary, fontSize: 13, fontFamily: T.mono, outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}
            </div>
          )}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <SectionHead>Deploy agent</SectionHead>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {["Create Retell AI agent from template", "Configure Twilio routing", "Register Denticon tool functions", "Run test call simulation"].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 6, background: T.navyLight, border: `1px solid ${T.navyBorder}` }}>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.green + "22", border: `1px solid ${T.green}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.green }}>✓</div>
                    <span style={{ fontSize: 12, fontFamily: T.sans, color: T.textPrimary }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
              <div style={{ fontSize: 20, fontFamily: T.sans, fontWeight: 700, color: T.green, marginBottom: 8 }}>Site created successfully</div>
              <div style={{ fontSize: 13, fontFamily: T.mono, color: T.textMuted }}>The agent is live and routing calls to Denticon.</div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, paddingTop: 16, borderTop: `1px solid ${T.navyBorder}` }}>
            {step > 0 && step < 3 ? (
              <button onClick={() => setStep(s => s - 1)} style={{ padding: "9px 20px", borderRadius: 6, border: `1px solid ${T.navyBorder}`, background: "none", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: T.mono }}>← Back</button>
            ) : <div />}
            {step < 3 ? (
              <button onClick={() => step === 2 ? handleDeploy() : setStep(s => s + 1)} style={{ padding: "9px 20px", borderRadius: 6, border: "none", background: T.amber, color: T.navy, cursor: "pointer", fontSize: 12, fontFamily: T.mono, fontWeight: 700 }}>
                {step === 2 ? (saving ? "Saving…" : "Deploy →") : "Continue →"}
              </button>
            ) : (
              <button onClick={onBack} style={{ padding: "9px 20px", borderRadius: 6, border: "none", background: T.green, color: "#fff", cursor: "pointer", fontSize: 12, fontFamily: T.mono, fontWeight: 700 }}>Done</button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── TEMPLATES PAGE ────────────────────────────────────────────────────────────
function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [promptTab, setPromptTab] = useState("prompt");
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [creating, setCreating]   = useState(false);
  const [editedPrompt, setEditedPrompt] = useState("");

  useEffect(() => {
    fetchLiveTemplates().then(data => {
      setTemplates(data);
      if (data.length > 0 && !selected) {
        setSelected(data[0].id);
        setEditedPrompt(data[0].system_prompt || "");
      }
    });
  }, []);

  const tmpl = templates.find(t => t.id === selected);

  useEffect(() => {
    if (tmpl) setEditedPrompt(tmpl.system_prompt || "");
  }, [selected]);

  const handleSave = async () => {
    if (!tmpl) return;
    setSaving(true);
    await saveTemplate(tmpl.id, { system_prompt: editedPrompt });
    setTemplates(prev => prev.map(t =>
      t.id === tmpl.id ? { ...t, system_prompt: editedPrompt } : t
    ));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleNew = async () => {
    setCreating(true);
    const newTpl = await createTemplate({
      name: "New Template",
      version: "1.0",
      published: false,
      system_prompt: "You are {{site.name}}'s dental receptionist.",
      llm_model: "gpt-4.1",
      temperature: "0.3",
      max_duration: 600,
      tools: ["search_patient","check_availability","book_appointment","reschedule_appointment","cancel_appointment","check_insurance_eligibility","transfer_to_human"],
      flows: ["book_appointment","cancel_appointment","reschedule_appointment","insurance_question","new_patient_intake","office_hours_directions","payment_billing","emergency"],
      compliance: { recording_consent: true, phi_redaction: true, retention_days: 2555, audit_tool_calls: true },
    });
    setTemplates(prev => [newTpl, ...prev]);
    setSelected(newTpl.id);
    setCreating(false);
  };

  const handlePublishToggle = async () => {
    if (!tmpl) return;
    const updated = await saveTemplate(tmpl.id, { published: tmpl.published ? 0 : 1 });
    setTemplates(prev => prev.map(t => t.id === tmpl.id ? { ...t, published: !t.published } : t));
  };

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* Template list */}
      <div style={{ width: 280, background: T.navy, borderRight: `1px solid ${T.navyBorder}`, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.navyBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 13, fontFamily: T.sans, fontWeight: 700, color: T.textPrimary }}>
            Templates <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textMuted }}>({templates.length})</span>
          </div>
          <button onClick={handleNew} style={{ fontSize: 12, fontFamily: T.mono, padding: "5px 10px", borderRadius: 5, border: `1px solid ${T.amber}55`, background: T.amberGlow, color: T.amber, cursor: "pointer" }}>
            {creating ? "…" : "+ New"}
          </button>
        </div>
        <div style={{ overflow: "auto", flex: 1 }}>
          {templates.map(t => (
            <div key={t.id} onClick={() => setSelected(t.id)} style={{
              padding: "14px 16px", borderBottom: `1px solid ${T.navyBorder}`,
              cursor: "pointer",
              background: selected === t.id ? T.navyLight : "transparent",
              borderLeft: selected === t.id ? `2px solid ${T.amber}` : "2px solid transparent",
            }}>
              <div style={{ fontSize: 13, fontFamily: T.sans, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>{t.name}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Badge color={t.published ? T.green : T.amber}>{t.published ? "PUBLISHED" : "DRAFT"}</Badge>
                <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>v{t.version}</span>
                <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>{Array.isArray(t.tools) ? t.tools.length : 0} tools</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Template editor */}
      {tmpl ? (
        <div style={{ flex: 1, overflow: "auto" }}>
          <TopBar title={tmpl.name} sub={`VERSION ${tmpl.version} · MODEL ${tmpl.llm_model} · UPDATED ${new Date(tmpl.updated_at).toLocaleDateString()}`}>
            <button onClick={handlePublishToggle} style={{
              padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: T.mono, fontWeight: 600,
              border: `1px solid ${tmpl.published ? T.green + "55" : T.amber + "55"}`,
              background: tmpl.published ? T.green + "11" : T.amberGlow,
              color: tmpl.published ? T.green : T.amber,
            }}>{tmpl.published ? "✓ Published" : "Publish"}</button>
            <PushButton label="↑ Push to All Sites" />
          </TopBar>

          <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.navyBorder}`, background: T.navy, padding: "0 24px" }}>
            {["prompt","tools","flows","compliance"].map(tab => (
              <button key={tab} onClick={() => setPromptTab(tab)} style={{
                padding: "12px 16px", border: "none", background: "none", cursor: "pointer",
                fontSize: 12, fontFamily: T.mono, fontWeight: 600,
                color: promptTab === tab ? T.amber : T.textSecondary,
                borderBottom: promptTab === tab ? `2px solid ${T.amber}` : "2px solid transparent",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>{tab}</button>
            ))}
          </div>

          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            {promptTab === "prompt" && (
              <>
                <Card>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <SectionHead>System prompt</SectionHead>
                    <button onClick={handleSave} style={{
                      padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                      fontSize: 11, fontFamily: T.mono, fontWeight: 700,
                      border: `1px solid ${saved ? T.green + "55" : T.amber + "55"}`,
                      background: saved ? T.green + "11" : T.amberGlow,
                      color: saved ? T.green : T.amber,
                    }}>{saving ? "Saving…" : saved ? "✓ Saved" : "Save"}</button>
                  </div>
                  <textarea
                    value={editedPrompt}
                    onChange={e => setEditedPrompt(e.target.value)}
                    style={{
                      width: "100%", height: 280, padding: 14, boxSizing: "border-box",
                      background: T.navy, border: `1px solid ${T.navyBorder}`,
                      borderRadius: 6, color: T.textPrimary, fontSize: 12,
                      fontFamily: T.mono, lineHeight: 1.7, resize: "vertical", outline: "none",
                    }} />
                </Card>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Card>
                    <SectionHead>LLM settings</SectionHead>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {[
                        ["Model", tmpl.llm_model],
                        ["Temperature", tmpl.temperature],
                        ["Max duration", `${tmpl.max_duration}s`],
                      ].map(([k, v]) => (
                        <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 12, color: T.textSecondary }}>{k}</span>
                          <span style={{ fontSize: 12, fontFamily: T.mono, color: T.teal }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                  <Card>
                    <SectionHead>Voice settings</SectionHead>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {[
                        ["Voice ID", tmpl.voice_id || "Not set"],
                        ["Language", "EN + ES"],
                        ["Version", `v${tmpl.version}`],
                      ].map(([k, v]) => (
                        <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 12, color: T.textSecondary }}>{k}</span>
                          <span style={{ fontSize: 12, fontFamily: T.mono, color: v === "Not set" ? T.textMuted : T.teal }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              </>
            )}

            {promptTab === "tools" && (
              <Card>
                <SectionHead>Registered Denticon tools ({Array.isArray(tmpl.tools) ? tmpl.tools.length : 0})</SectionHead>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {(Array.isArray(tmpl.tools) ? tmpl.tools : []).map(tool => (
                    <div key={tool} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 14px", borderRadius: 6,
                      background: T.navyLight, border: `1px solid ${T.navyBorder}`,
                    }}>
                      <StatusDot status="live" />
                      <span style={{ fontSize: 12, fontFamily: T.mono, color: T.teal, flex: 1 }}>{tool}</span>
                      <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>→ /dv-api/denticon/&#123;site_id&#125;</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {promptTab === "flows" && (
              <Card>
                <SectionHead>Conversation intents ({Array.isArray(tmpl.flows) ? tmpl.flows.length : 0})</SectionHead>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {(Array.isArray(tmpl.flows) ? tmpl.flows : []).map(intent => (
                    <div key={intent} style={{
                      padding: "10px 14px", borderRadius: 6,
                      background: T.navyLight, border: `1px solid ${T.navyBorder}`,
                      fontSize: 12, fontFamily: T.mono, color: T.textSecondary,
                    }}>{intent}</div>
                  ))}
                </div>
              </Card>
            )}

            {promptTab === "compliance" && (
              <Card>
                <SectionHead>Compliance rules</SectionHead>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {tmpl.compliance && typeof tmpl.compliance === "object" ? (
                    Object.entries(tmpl.compliance).map(([k, v]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 6, background: T.navyLight, border: `1px solid ${T.navyBorder}` }}>
                        <span style={{ fontSize: 12, color: T.textSecondary }}>{k.replace(/_/g, " ")}</span>
                        <Badge color={v === true ? T.green : v === false ? T.red : T.teal}>
                          {v === true ? "ENABLED" : v === false ? "DISABLED" : String(v)}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: T.textMuted, fontSize: 13 }}>No compliance rules set.</div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted, fontFamily: T.mono, fontSize: 13 }}>
          {templates.length === 0 ? "Loading templates…" : "Select a template"}
        </div>
      )}
    </div>
  );
}

// ─── CALL LOGS PAGE ────────────────────────────────────────────────────────────
function CallLogsPage() {
  const [selected, setSelected]         = useState(null);
  const [calls, setCalls]               = useState([]);
  const [sites, setSites]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filterSite, setFilterSite]     = useState("all");
  const [filterOutcome, setFilterOutcome] = useState("all");
  const [filterDate, setFilterDate]     = useState("");
  const [search, setSearch]             = useState("");

  useEffect(() => {
    Promise.all([fetchLiveCalls(), fetchLiveSites()]).then(([callData, siteData]) => {
      setCalls(callData);
      setSites(Array.isArray(siteData) ? siteData : []);
      setLoading(false);
    });
    const t = setInterval(() => fetchLiveCalls().then(setCalls), 30000);
    return () => clearInterval(t);
  }, []);

  const call = selected ? calls.find(c => c.id === selected) : null;

  const filtered = calls.filter(c => {
    if (filterSite !== "all" && (c.site_name || c.site) !== filterSite) return false;
    if (filterOutcome !== "all" && c.outcome !== filterOutcome) return false;
    if (filterDate) {
      const callDate = (c.timestamp || c.created_at || "").slice(0, 10);
      if (callDate !== filterDate) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!(c.site_name || c.site || "").toLowerCase().includes(q) &&
          !(c.patient || "").toLowerCase().includes(q) &&
          !(c.from_number || "").toLowerCase().includes(q) &&
          !(c.conf || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const inputStyle = { padding: "6px 10px", borderRadius: 5, fontSize: 11, fontFamily: T.mono, border: `1px solid ${T.navyBorder}`, background: T.navyLight, color: T.textPrimary, outline: "none" };
  const selectStyle = { ...inputStyle, cursor: "pointer" };
  const OUTCOMES = ["all","booked","booking_intent","recovered_booking","info_only","transferred","cancelled","rescheduled","unknown"];

  const outcomeColor = o =>
    o === "booked"            ? T.green  :
    o === "booking_intent"    ? T.teal   :
    o === "recovered_booking" ? T.purple :
    o === "transferred"       ? T.amber  :
    o === "cancelled"         ? T.red    :
    o === "rescheduled"       ? T.purple :
    T.textMuted;

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        <TopBar title="Call Logs" sub={`${filtered.length} OF ${calls.length} CALLS`} />

        {/* Filter bar */}
        <div style={{ display: "flex", gap: 10, padding: "10px 20px", flexWrap: "wrap", borderBottom: `1px solid ${T.navyBorder}`, background: T.navy, alignItems: "center" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patient, site, phone, conf#..." style={{ ...inputStyle, width: 240 }} />
          <select value={filterSite} onChange={e => setFilterSite(e.target.value)} style={selectStyle}>
            <option value="all">All sites</option>
            {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <select value={filterOutcome} onChange={e => setFilterOutcome(e.target.value)} style={selectStyle}>
            {OUTCOMES.map(o => <option key={o} value={o}>{o === "all" ? "All outcomes" : o.replace(/_/g, " ").toUpperCase()}</option>)}
          </select>
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{ ...inputStyle, colorScheme: "dark" }} />
          {(filterSite !== "all" || filterOutcome !== "all" || filterDate || search) && (
            <button onClick={() => { setFilterSite("all"); setFilterOutcome("all"); setFilterDate(""); setSearch(""); }} style={{ padding: "6px 12px", borderRadius: 5, fontSize: 11, fontFamily: T.mono, border: `1px solid ${T.red}44`, background: T.red + "11", color: T.red, cursor: "pointer" }}>✕ Clear</button>
          )}
          <span style={{ marginLeft: "auto", fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Column headers */}
        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 160px 70px 140px 120px", gap: 12, padding: "10px 20px", fontSize: 10, fontFamily: T.mono, color: T.textMuted, letterSpacing: "0.08em", borderBottom: `1px solid ${T.navyBorder}`, background: T.navyLight }}>
          <span>TIME</span><span>PATIENT</span><span>SITE</span><span>DUR.</span><span>OUTCOME</span><span>CONF #</span>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: T.mono, fontSize: 13 }}>Loading calls…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: T.mono, fontSize: 13 }}>No calls match your filters.</div>
        ) : (
          filtered.map(c => (
            <div key={c.id} onClick={() => setSelected(c.id === selected ? null : c.id)} style={{
              display: "grid", gridTemplateColumns: "80px 1fr 160px 70px 140px 120px",
              gap: 12, padding: "12px 20px", borderBottom: `1px solid ${T.navyBorder}`,
              cursor: "pointer", fontSize: 12,
              background: selected === c.id ? T.navyLight : "transparent", transition: "background 0.1s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = T.navyLight}
            onMouseLeave={e => e.currentTarget.style.background = selected === c.id ? T.navyLight : "transparent"}
            >
              <span style={{ fontFamily: T.mono, color: T.textMuted, fontSize: 11 }}>{c.call_time || c.time || "—"}</span>
              <span style={{ color: T.textPrimary, fontFamily: T.sans, fontWeight: 500 }}>{c.patient || "Unknown"}</span>
              <span style={{ color: T.textSecondary, fontSize: 11 }}>{c.site_name || c.site || "—"}</span>
              <span style={{ fontFamily: T.mono, color: T.textMuted }}>{c.duration || "—"}</span>
              <Badge color={outcomeColor(c.outcome)}>{(c.outcome || "unknown").replace(/_/g, " ").toUpperCase()}</Badge>              {c.conf ? <span style={{ fontFamily: T.mono, fontSize: 11, color: T.teal }}>{c.conf}</span> : <span style={{ color: T.textDim }}>—</span>}
            </div>
          ))
        )}
      </div>

      {/* Call detail panel */}
      {call && (
        <div style={{ width: 360, borderLeft: `1px solid ${T.navyBorder}`, background: T.navyMid, overflow: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 15, fontFamily: T.sans, fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>{call.patient || "Unknown"}</div>
              <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>{call.site_name || call.site || "—"}</div>
            </div>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>

          {/* Core call info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              ["Time", call.call_time || call.time || "—"],
              ["Duration", call.duration || "—"],
              ["Outcome", (call.outcome || "—").replace(/_/g, " ")],
              ["Direction", call.direction || "inbound"],
              ["From", call.from_number || "—"],
              ["To", call.to_number || "—"],
            ].map(([k, v]) => (
              <div key={k} style={{ padding: "10px 12px", background: T.navyLight, borderRadius: 6, border: `1px solid ${T.navyBorder}` }}>
                <div style={{ fontSize: 10, fontFamily: T.mono, color: T.textMuted, marginBottom: 4 }}>{k.toUpperCase()}</div>
                <div style={{ fontSize: 12, fontFamily: T.mono, color: T.textPrimary }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Post Call Data Extraction */}
          {(call.intent || call.appointment_date || call.appointment_time || call.appointment_requested == 1) && (
            <div style={{ background: T.navyLight, border: `1px solid ${T.teal}33`, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, fontFamily: T.mono, color: T.teal, marginBottom: 10, letterSpacing: "0.08em" }}>POST CALL ANALYSIS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {call.intent && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>Intent</span>
                    <Badge color={outcomeColor(call.intent === "book_appointment" ? "booking_intent" : call.intent)}>{call.intent.replace(/_/g, " ").toUpperCase()}</Badge>
                  </div>
                )}
                {call.appointment_requested == 1 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>Appt requested</span>
                    <Badge color={T.green}>YES</Badge>
                  </div>
                )}
                {call.appointment_date && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>Date preference</span>
                    <span style={{ fontSize: 12, fontFamily: T.mono, color: T.textPrimary }}>{call.appointment_date}</span>
                  </div>
                )}
                {call.appointment_time && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>Time preference</span>
                    <span style={{ fontSize: 12, fontFamily: T.mono, color: T.textPrimary }}>{call.appointment_time}</span>
                  </div>
                )}
                {call.call_successful == 1 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted }}>Call successful</span>
                    <Badge color={T.green}>YES</Badge>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirmation */}
          {call.conf && (
            <div style={{ padding: "10px 12px", background: T.green + "11", border: `1px solid ${T.green}33`, borderRadius: 6 }}>
              <div style={{ fontSize: 10, fontFamily: T.mono, color: T.green, marginBottom: 4 }}>CONFIRMATION #</div>
              <div style={{ fontSize: 13, fontFamily: T.mono, color: T.green }}>{call.conf}</div>
            </div>
          )}

          {/* Callback status */}
          {call.outcome === "booking_intent" && !call.conf && (
            <div style={{ padding: "10px 12px", background: T.amber + "11", border: `1px solid ${T.amber}33`, borderRadius: 6 }}>
              <div style={{ fontSize: 10, fontFamily: T.mono, color: T.amber, marginBottom: 4 }}>MISSED CALL RECOVERY</div>
              <div style={{ fontSize: 12, fontFamily: T.sans, color: T.textSecondary }}>
                Patient expressed booking intent but did not confirm. A callback has been queued automatically.
              </div>
            </div>
          )}
          {call.outcome === "recovered_booking" && (
            <div style={{ padding: "10px 12px", background: T.green + "11", border: `1px solid ${T.green}33`, borderRadius: 6 }}>
              <div style={{ fontSize: 10, fontFamily: T.mono, color: T.green, marginBottom: 4 }}>✓ RECOVERED BOOKING</div>
              <div style={{ fontSize: 12, fontFamily: T.sans, color: T.textSecondary }}>
                Patient was called back and booked an appointment.
              </div>
            </div>
          )}

          {/* Summary + transcript */}
          <div>
            {call.call_summary && (
              <div style={{ padding: "10px 12px", background: T.navyLight, border: `1px solid ${T.navyBorder}`, borderRadius: 6, marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontFamily: T.mono, color: T.textMuted, marginBottom: 4 }}>CALL SUMMARY</div>
                <div style={{ fontSize: 12, fontFamily: T.sans, color: T.textSecondary, lineHeight: 1.6 }}>{call.call_summary}</div>
              </div>
            )}
            <SectionHead>Transcript preview</SectionHead>
            <div style={{ background: T.navy, border: `1px solid ${T.navyBorder}`, borderRadius: 6, padding: 12, fontSize: 11, fontFamily: T.mono, lineHeight: 1.8, color: T.textSecondary, maxHeight: 220, overflow: "auto" }}>
              {call.transcript ? (
                call.transcript.split("\n").filter(line => line.trim()).map((line, i) => {
                  const isAgent = line.toLowerCase().startsWith("agent:");
                  const isUser  = line.toLowerCase().startsWith("user:") || line.toLowerCase().startsWith("patient:");
                  return (
                    <div key={i} style={{ marginTop: i === 0 ? 0 : 8 }}>
                      {isAgent && <span><span style={{ color: T.amber }}>Agent: </span><span style={{ color: T.textSecondary }}>{line.replace(/^agent:\s*/i, "")}</span></span>}
                      {isUser  && <span><span style={{ color: T.teal }}>Patient: </span><span style={{ color: T.textSecondary }}>{line.replace(/^(user|patient):\s*/i, "")}</span></span>}
                      {!isAgent && !isUser && <span style={{ color: T.textMuted }}>{line}</span>}
                    </div>
                  );
                })
              ) : (
                <div style={{ color: T.textMuted }}>
                  {call.status === "in_progress" ? "Call in progress — transcript will appear when call ends." : "No transcript available for this call."}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { call.transcript ? window.open(`${API_BASE}/calls/${call.id}/transcript?key=${API_KEY}`, "_blank") : alert("No transcript available yet."); }} style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: `1px solid ${T.navyBorder}`, background: call.transcript ? T.navySurface : T.navyLight, color: call.transcript ? T.teal : T.textMuted, fontSize: 11, fontFamily: T.mono, cursor: "pointer" }}>Full Transcript</button>
            <button onClick={() => { call.recording_url ? window.open(call.recording_url, "_blank") : alert("No recording available yet."); }} style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: `1px solid ${T.navyBorder}`, background: call.recording_url ? T.navySurface : T.navyLight, color: call.recording_url ? T.teal : T.textMuted, fontSize: 11, fontFamily: T.mono, cursor: "pointer" }}>Audio Recording</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── ANALYTICS PAGE ────────────────────────────────────────────────────────────
function AnalyticsPage() {
  const [data, setData]       = useState(null);
  const [calls, setCalls]     = useState([]);
  const [sites, setSites]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange]     = useState("30");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/stats`, { headers: HEADERS }).then(r => r.json()),
      fetch(`${API_BASE}/calls?limit=500`, { headers: HEADERS }).then(r => r.json()),
      fetch(`${API_BASE}/sites`, { headers: HEADERS }).then(r => r.json()),
    ]).then(([stats, callData, siteData]) => {
      setData(stats);
      setCalls(callData.calls || []);
      setSites(Array.isArray(siteData) ? siteData : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Compute per-site stats from real calls
  const siteStats = sites.map(site => {
    const siteCalls = calls.filter(c =>
      c.site_name === site.name || c.site_name === site.name || c.site === site.name
    );
    const booked    = siteCalls.filter(c => c.outcome === "booked").length;
    const escalated = siteCalls.filter(c => c.escalated).length;
    const total     = siteCalls.length;
    const conv      = total ? Math.round((booked / total) * 1000) / 10 : 0;
    const esc       = total ? Math.round((escalated / total) * 1000) / 10 : 0;
    return { name: site.name, calls: total, booked, conv, esc };
  }).filter(s => s.calls > 0).sort((a, b) => b.calls - a.calls);

  // Compute outcome breakdown
  const totalCalls    = calls.length;
  const totalBooked   = calls.filter(c => c.outcome === "booked").length;
  const totalEsc      = calls.filter(c => c.escalated).length;
  const totalCancelled = calls.filter(c => c.outcome === "cancelled").length;
  const bookingRate   = totalCalls ? Math.round((totalBooked / totalCalls) * 1000) / 10 : 0;
  const escRate       = totalCalls ? Math.round((totalEsc / totalCalls) * 1000) / 10 : 0;

  // Avg duration
  const durations   = calls.filter(c => c.duration_sec).map(c => parseInt(c.duration_sec));
  const avgDurSec   = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
  const avgDur      = `${Math.floor(avgDurSec / 60)}:${String(avgDurSec % 60).padStart(2, "0")}`;

  // ROI estimate ($285 avg appointment value)
  const AVG_APPT_VALUE = 285;
  const estRevenue    = totalBooked * AVG_APPT_VALUE;

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{ fontFamily: T.mono, color: T.textMuted, fontSize: 13 }}>Loading analytics…</div>
    </div>
  );

  return (
    <div style={{ overflow: "auto" }}>
      <TopBar title="Analytics" sub={`ALL CALLS · ${totalCalls} TOTAL RECORDS`}>
        <div style={{ display: "flex", gap: 6 }}>
          {["7","30","90"].map(d => (
            <button key={d} onClick={() => setRange(d)} style={{
              padding: "5px 10px", borderRadius: 5, fontSize: 11, fontFamily: T.mono,
              border: `1px solid ${range === d ? T.amber : T.navyBorder}`,
              background: range === d ? T.amberGlow : "none",
              color: range === d ? T.amber : T.textSecondary, cursor: "pointer",
            }}>{d}d</button>
          ))}
        </div>
      </TopBar>
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
          {[
            { label: "Total calls", value: totalCalls.toLocaleString(), sub: `${sites.length} active sites`, color: T.teal },
            { label: "Bookings made", value: totalBooked.toLocaleString(), sub: `${bookingRate}% conversion`, color: T.green },
            { label: "Escalation rate", value: `${escRate}%`, sub: `${totalEsc} escalated calls`, color: T.amber },
            { label: "Avg call duration", value: avgDur, sub: "Per completed call", color: T.textSecondary },
            { label: "Cancellations", value: totalCancelled.toLocaleString(), sub: "Patient requested", color: T.textMuted },
          ].map((m, i) => (
            <Card key={i}><Metric {...m} /></Card>
          ))}
        </div>

        {/* Outcome breakdown */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Card>
            <SectionHead>Call outcome breakdown</SectionHead>
            {[
              { label: "Booked",      value: totalBooked,    color: T.green },
              { label: "Info only",   value: calls.filter(c => c.outcome === "info_only").length, color: T.teal },
              { label: "Transferred", value: totalEsc,       color: T.amber },
              { label: "Cancelled",   value: totalCancelled, color: T.textMuted },
              { label: "Rescheduled", value: calls.filter(c => c.outcome === "rescheduled").length, color: T.purple },
              { label: "Unknown",     value: calls.filter(c => c.outcome === "unknown" || !c.outcome).length, color: T.textDim },
            ].map((item, i) => {
              const pct = totalCalls ? Math.round((item.value / totalCalls) * 100) : 0;
              return (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: T.textSecondary }}>{item.label}</span>
                    <span style={{ fontSize: 12, fontFamily: T.mono, color: item.color }}>{item.value} <span style={{ color: T.textMuted }}>({pct}%)</span></span>
                  </div>
                  <div style={{ height: 4, background: T.navyLight, borderRadius: 2 }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: item.color, borderRadius: 2, transition: "width 0.5s ease" }} />
                  </div>
                </div>
              );
            })}
          </Card>

          <Card glow>
            <SectionHead>Estimated ROI</SectionHead>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { label: "Appointments booked by AI", value: totalBooked.toLocaleString(), color: T.teal },
                { label: "Avg value per appointment", value: `$${AVG_APPT_VALUE}`, color: T.amber },
                { label: "Est. revenue attributed", value: `$${(estRevenue).toLocaleString()}`, color: T.green },
                { label: "Total calls handled", value: totalCalls.toLocaleString(), color: T.textSecondary },
              ].map((m, i) => <Metric key={i} {...m} />)}
            </div>
            {totalBooked > 0 && (
              <div style={{ marginTop: 16, padding: "10px 14px", background: T.green + "11", border: `1px solid ${T.green}33`, borderRadius: 6 }}>
                <span style={{ fontSize: 12, fontFamily: T.mono, color: T.green }}>
                  {bookingRate}% booking conversion · ${(estRevenue).toLocaleString()} estimated revenue
                </span>
              </div>
            )}
          </Card>
        </div>

        {/* Today's stats from /stats endpoint */}
        {data && (
          <Card>
            <SectionHead>Today vs this month</SectionHead>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
              {[
                { label: "Calls today", value: String(data.today?.total_calls ?? 0), sub: `${data.today?.booking_rate ?? 0}% booked`, color: T.teal },
                { label: "Booked today", value: String(data.today?.booked ?? 0), sub: `${data.today?.escalated ?? 0} escalated`, color: T.green },
                { label: "This month total", value: String(data.month?.total_calls ?? 0), sub: `${data.month?.booked ?? 0} bookings`, color: T.amber },
              ].map((m, i) => <Metric key={i} {...m} />)}
            </div>
          </Card>
        )}

        {/* Per-site performance */}
        <Card>
          <SectionHead>Performance by site (all time)</SectionHead>
          {siteStats.length === 0 ? (
            <div style={{ color: T.textMuted, fontSize: 13, padding: "20px 0", textAlign: "center", fontFamily: T.mono }}>
              No call data yet — calls will appear here once Retell starts sending events.
            </div>
          ) : (
            <div>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 80px 80px 100px 100px 90px",
                gap: 12, padding: "8px 12px",
                fontSize: 10, fontFamily: T.mono, color: T.textMuted, letterSpacing: "0.08em",
                borderBottom: `1px solid ${T.navyBorder}`,
              }}>
                <span>SITE</span>
                <span style={{ textAlign: "right" }}>CALLS</span>
                <span style={{ textAlign: "right" }}>BOOKED</span>
                <span style={{ textAlign: "right" }}>CONV %</span>
                <span style={{ textAlign: "right" }}>ESC %</span>
                <span style={{ textAlign: "right" }}>TREND</span>
              </div>
              {siteStats.map((s, i) => (
                <div key={i} style={{
                  display: "grid", gridTemplateColumns: "1fr 80px 80px 100px 100px 90px",
                  gap: 12, padding: "11px 12px",
                  borderBottom: `1px solid ${T.navyBorder}`,
                  fontSize: 12,
                }}>
                  <span style={{ color: T.textPrimary, fontFamily: T.sans }}>{s.name}</span>
                  <span style={{ fontFamily: T.mono, color: T.teal, textAlign: "right" }}>{s.calls.toLocaleString()}</span>
                  <span style={{ fontFamily: T.mono, color: T.green, textAlign: "right" }}>{s.booked}</span>
                  <span style={{ fontFamily: T.mono, textAlign: "right", color: s.conv > 35 ? T.green : s.conv > 20 ? T.amber : T.red }}>{s.conv}%</span>
                  <span style={{ fontFamily: T.mono, textAlign: "right", color: s.esc < 6 ? T.green : s.esc < 10 ? T.amber : T.red }}>{s.esc}%</span>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 2, justifyContent: "flex-end" }}>
                    {[0.4,0.6,0.5,0.8,0.7,0.9,1.0].map((v, j) => (
                      <div key={j} style={{ width: 6, background: T.teal + "88", borderRadius: "1px 1px 0 0", height: `${v * 20}px` }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}

// ─── CALLBACKS PAGE ────────────────────────────────────────────────────────────
function CallbacksPage() {
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("all");

  useEffect(() => {
    fetchLiveCallbacks().then(data => { setCallbacks(data); setLoading(false); });
    const t = setInterval(() => fetchLiveCallbacks().then(setCallbacks), 30000);
    return () => clearInterval(t);
  }, []);

  const filtered = filter === "all" ? callbacks : callbacks.filter(c => c.status === filter);

  const counts = {
    pending: callbacks.filter(c => c.status === "pending").length,
    called:  callbacks.filter(c => c.status === "called").length,
    booked:  callbacks.filter(c => c.status === "booked").length,
    error:   callbacks.filter(c => c.status === "error").length,
  };

  const statusColor = s =>
    s === "booked"  ? T.green :
    s === "called"  ? T.teal  :
    s === "pending" ? T.amber :
    s === "error"   ? T.red   :
    s === "skipped" ? T.textMuted : T.textMuted;

  return (
    <div style={{ overflow: "auto" }}>
      <TopBar title="Missed Call Recovery" sub={`${callbacks.length} TOTAL · ${counts.booked} RECOVERED`} />
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {[
            { label: "Pending callbacks",   value: String(counts.pending), sub: "Queued to fire",        color: T.amber },
            { label: "Callbacks made",      value: String(counts.called),  sub: "Called back",           color: T.teal },
            { label: "Bookings recovered",  value: String(counts.booked),  sub: "Patient rebooked",      color: T.green },
            { label: "Errors",              value: String(counts.error),   sub: "Failed to connect",     color: T.red },
          ].map((m, i) => <Card key={i}><Metric {...m} large /></Card>)}
        </div>

        {/* Filter tabs */}
        <Card>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {["all","pending","called","booked","error"].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: "5px 14px", borderRadius: 5, fontSize: 11, fontFamily: T.mono,
                border: `1px solid ${filter === f ? statusColor(f) : T.navyBorder}`,
                background: filter === f ? statusColor(f) + "22" : "none",
                color: filter === f ? statusColor(f) : T.textSecondary,
                cursor: "pointer", fontWeight: 600,
              }}>{f.toUpperCase()} {f !== "all" && counts[f] > 0 ? `(${counts[f]})` : ""}</button>
            ))}
            <span style={{ marginLeft: "auto", fontSize: 11, fontFamily: T.mono, color: T.textMuted, alignSelf: "center" }}>
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Table header */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 160px 140px 140px 120px 100px",
            gap: 12, padding: "8px 12px",
            fontSize: 10, fontFamily: T.mono, color: T.textMuted, letterSpacing: "0.08em",
            borderBottom: `1px solid ${T.navyBorder}`,
          }}>
            <span>PATIENT</span><span>SITE</span><span>PHONE</span><span>APPT REQUEST</span><span>SCHEDULED</span><span>STATUS</span>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: T.mono, fontSize: 13 }}>Loading callbacks…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: T.mono, fontSize: 13 }}>
              {filter === "all" ? "No callbacks yet — they'll appear here when patients hang up before booking." : `No ${filter} callbacks.`}
            </div>
          ) : (
            filtered.map(cb => (
              <div key={cb.id} style={{
                display: "grid", gridTemplateColumns: "1fr 160px 140px 140px 120px 100px",
                gap: 12, padding: "12px 12px", fontSize: 12,
                borderBottom: `1px solid ${T.navyBorder}`, alignItems: "center",
              }}>
                <span style={{ color: T.textPrimary, fontFamily: T.sans, fontWeight: 500 }}>{cb.patient_name || "Unknown"}</span>
                <span style={{ color: T.textSecondary, fontSize: 11 }}>{cb.site_name || "—"}</span>
                <span style={{ fontFamily: T.mono, color: T.textMuted, fontSize: 11 }}>{cb.from_number || "—"}</span>
                <div style={{ fontSize: 11, color: T.textSecondary }}>
                  {cb.appointment_date && <div>{cb.appointment_date}</div>}
                  {cb.appointment_time && <div style={{ color: T.textMuted }}>{cb.appointment_time}</div>}
                  {!cb.appointment_date && !cb.appointment_time && <span style={{ color: T.textDim }}>—</span>}
                </div>
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textMuted }}>
                  {cb.scheduled_at ? new Date(cb.scheduled_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
                <Badge color={statusColor(cb.status)}>{(cb.status || "pending").toUpperCase()}</Badge>
              </div>
            ))
          )}
        </Card>

      </div>
    </div>
  );
}

// ─── COMPLIANCE PAGE ───────────────────────────────────────────────────────────
function CompliancePage() {
  return (
    <div style={{ overflow: "auto" }}>
      <TopBar title="Compliance & Audit" sub="HIPAA · SOC 2 · AZURE POLICY" />
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {[
            { label: "Azure Policy compliance", value: "100%", sub: "0 violations", color: T.green },
            { label: "BAAs on file", value: "3 / 3", sub: "Microsoft · Retell · Twilio", color: T.green },
            { label: "PHI redactions (30d)", value: "3,847", sub: "All transcripts processed", color: T.teal },
            { label: "Audit log entries (30d)", value: "14,291", sub: "WORM-locked", color: T.teal },
          ].map((m, i) => <Card key={i}><Metric {...m} /></Card>)}
        </div>

        <Card>
          <SectionHead>HIPAA control status</SectionHead>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { control: "Microsoft HIPAA BAA signed", status: "ok" },
              { control: "Entra ID MFA — all users", status: "ok" },
              { control: "PostgreSQL RLS — tenant isolation", status: "ok" },
              { control: "Azure Key Vault HSM — CMK encryption", status: "ok" },
              { control: "Blob Storage immutability — 7-year WORM", status: "ok" },
              { control: "Private endpoints — no public DB/storage", status: "ok" },
              { control: "PHI redaction (Presidio) — all transcripts", status: "ok" },
              { control: "Recording consent collected", status: "ok" },
              { control: "Retell AI BAA", status: "ok" },
              { control: "Twilio HIPAA BAA", status: "ok" },
              { control: "SOC 2 Type II audit", status: "in_progress" },
              { control: "HITRUST CSF certification", status: "planned" },
            ].map((item, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", borderRadius: 6,
                background: T.navyLight, border: `1px solid ${T.navyBorder}`,
              }}>
                <span style={{ fontSize: 12, fontFamily: T.sans, color: T.textPrimary }}>{item.control}</span>
                <Badge color={item.status === "ok" ? T.green : item.status === "in_progress" ? T.amber : T.textMuted}>
                  {item.status === "ok" ? "✓ COMPLIANT" : item.status === "in_progress" ? "IN PROGRESS" : "PLANNED"}
                </Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <SectionHead>Recent audit log</SectionHead>
          <div style={{ fontFamily: T.mono, fontSize: 11 }}>
            {[
              { time: "11:09", action: "call.completed", resource: "call/c8", user: "agent@system", details: "outcome=booked conf=APPT-4424" },
              { time: "11:06", action: "denticon.book_appointment", resource: "site/s2", user: "agent@system", details: "idempotency_key=sha256:abc123" },
              { time: "10:51", action: "call.escalated", resource: "call/c7", user: "agent@system", details: "reason=long_call_unresolved" },
              { time: "10:33", action: "call.completed", resource: "call/c6", user: "agent@system", details: "outcome=booked conf=APPT-4423" },
              { time: "09:22", action: "agent.push_config", resource: "site/s1", user: "jane.doe@jeffersondental.com", details: "version=2.1 status=deployed" },
            ].map((entry, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 180px 120px 140px 1fr", gap: 12, padding: "8px 4px", borderBottom: `1px solid ${T.navyBorder}` }}>
                <span style={{ color: T.textMuted }}>{entry.time}</span>
                <span style={{ color: T.amber }}>{entry.action}</span>
                <span style={{ color: T.teal }}>{entry.resource}</span>
                <span style={{ color: T.textSecondary }}>{entry.user}</span>
                <span style={{ color: T.textMuted }}>{entry.details}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── PLACEHOLDER PAGE ──────────────────────────────────────────────────────────
function PlaceholderPage({ title }) {
  return (
    <div>
      <TopBar title={title} sub="JEFFERSON DENTAL" />
      <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: T.mono, fontSize: 13 }}>
        {title} — full implementation follows the same patterns as above.
      </div>
    </div>
  );
}

// ─── SETTINGS PAGE ─────────────────────────────────────────────────────────────
function SettingsPage() {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [success, setSuccess]     = useState("");
  const [form, setForm]           = useState({ name: "", email: "", password: "", role: "admin" });

  const loadUsers = () => {
    setLoading(true);
    fetch(`${API_BASE}/auth/users`, { headers: HEADERS })
      .then(r => r.json())
      .then(data => { setUsers(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/auth/users`, {
        method: "POST", headers: HEADERS,
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) { alert(data.error || "Failed to create user"); setSaving(false); return; }
      setSuccess("User created successfully");
      setForm({ name: "", email: "", password: "", role: "admin" });
      setShowForm(false);
      loadUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch { alert("Network error"); }
    setSaving(false);
  };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}?`)) return;
    await fetch(`${API_BASE}/auth/users/${id}`, { method: "DELETE", headers: HEADERS });
    loadUsers();
  };

  const ROLES = ["super_admin", "admin", "site_admin", "staff"];

  return (
    <div style={{ overflow: "auto" }}>
      <TopBar title="Settings" sub="USER MANAGEMENT · ACCESS CONTROL" />
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>

        {/* User list */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <SectionHead>Platform users ({users.filter(u => u.active).length} active)</SectionHead>
            <button onClick={() => setShowForm(s => !s)} style={{
              padding: "7px 14px", borderRadius: 6, cursor: "pointer",
              fontSize: 12, fontFamily: T.mono, fontWeight: 700,
              border: `1px solid ${T.amber}55`, background: T.amberGlow, color: T.amber,
            }}>
              {showForm ? "✕ Cancel" : "+ Add User"}
            </button>
          </div>

          {/* Add user form */}
          {showForm && (
            <form onSubmit={handleCreate} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 12, padding: 16, marginBottom: 16,
              background: T.navyLight, borderRadius: 8,
              border: `1px solid ${T.amber}33`,
            }}>
              {[
                ["Full name", "name", "text", "Jane Doe"],
                ["Email address", "email", "email", "jane@company.com"],
                ["Password", "password", "password", "••••••••"],
              ].map(([label, key, type, ph]) => (
                <div key={key}>
                  <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 5, letterSpacing: "0.06em" }}>{label.toUpperCase()}</div>
                  <input
                    type={type} required placeholder={ph}
                    value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 6, boxSizing: "border-box",
                      background: T.navy, border: `1px solid ${T.navyBorder}`,
                      color: T.textPrimary, fontSize: 13, fontFamily: T.sans, outline: "none",
                    }}
                  />
                </div>
              ))}
              <div>
                <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 5, letterSpacing: "0.06em" }}>ROLE</div>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={{
                  width: "100%", padding: "9px 12px", borderRadius: 6, boxSizing: "border-box",
                  background: T.navy, border: `1px solid ${T.navyBorder}`,
                  color: T.textPrimary, fontSize: 13, fontFamily: T.sans,
                }}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                {success && <span style={{ fontSize: 12, fontFamily: T.mono, color: T.green, alignSelf: "center" }}>✓ {success}</span>}
                <button type="submit" disabled={saving} style={{
                  padding: "9px 20px", borderRadius: 6, border: "none",
                  background: T.amber, color: T.navy, cursor: "pointer",
                  fontSize: 12, fontFamily: T.mono, fontWeight: 700,
                }}>
                  {saving ? "Creating…" : "Create User"}
                </button>
              </div>
            </form>
          )}

          {/* Users table */}
          {loading ? (
            <div style={{ color: T.textMuted, fontFamily: T.mono, fontSize: 13, padding: "20px 0", textAlign: "center" }}>Loading users…</div>
          ) : (
            <div>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 120px 140px 80px",
                gap: 12, padding: "8px 12px",
                fontSize: 10, fontFamily: T.mono, color: T.textMuted, letterSpacing: "0.08em",
                borderBottom: `1px solid ${T.navyBorder}`,
              }}>
                <span>NAME</span><span>EMAIL</span><span>ROLE</span><span>LAST LOGIN</span><span>ACTION</span>
              </div>
              {users.map(u => (
                <div key={u.id} style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 120px 140px 80px",
                  gap: 12, padding: "12px 12px", fontSize: 12,
                  borderBottom: `1px solid ${T.navyBorder}`,
                  opacity: u.active ? 1 : 0.4,
                }}>
                  <span style={{ color: T.textPrimary, fontFamily: T.sans, fontWeight: 500 }}>
                    {u.name || "—"}
                    {!u.active && <span style={{ fontSize: 10, color: T.red, marginLeft: 8, fontFamily: T.mono }}>INACTIVE</span>}
                  </span>
                  <span style={{ color: T.textSecondary, fontFamily: T.mono, fontSize: 11 }}>{u.email}</span>
                  <Badge color={
                    u.role === "super_admin" ? T.amber :
                    u.role === "admin" ? T.teal :
                    u.role === "site_admin" ? T.purple : T.textMuted
                  }>{u.role}</Badge>
                  <span style={{ color: T.textMuted, fontFamily: T.mono, fontSize: 11 }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never"}
                  </span>
                  {u.active ? (
                    <button onClick={() => handleDeactivate(u.id, u.name || u.email)} style={{
                      padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                      fontSize: 11, fontFamily: T.mono,
                      border: `1px solid ${T.red}44`, background: T.red + "11", color: T.red,
                    }}>Deactivate</button>
                  ) : (
                    <span style={{ fontSize: 11, fontFamily: T.mono, color: T.textDim }}>—</span>
                  )}
                </div>
              ))}
              {users.length === 0 && (
                <div style={{ color: T.textMuted, fontFamily: T.mono, fontSize: 13, padding: "20px 0", textAlign: "center" }}>
                  No users found.
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Role guide */}
        <Card>
          <SectionHead>Role permissions</SectionHead>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {[
              { role: "super_admin", color: T.amber, perms: ["Full platform access", "Manage all orgs", "Manage users", "Billing access"] },
              { role: "admin", color: T.teal, perms: ["Manage sites", "Manage templates", "View all calls", "View analytics"] },
              { role: "site_admin", color: T.purple, perms: ["Manage assigned sites", "View site calls", "Configure agents", "View analytics"] },
              { role: "staff", color: T.textMuted, perms: ["View assigned sites", "View call logs", "Read-only access", "No config changes"] },
            ].map(({ role, color, perms }) => (
              <div key={role} style={{ padding: "14px", background: T.navyLight, borderRadius: 8, border: `1px solid ${T.navyBorder}` }}>
                <Badge color={color} style={{ marginBottom: 10 }}>{role}</Badge>
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  {perms.map((p, i) => (
                    <div key={i} style={{ fontSize: 11, fontFamily: T.sans, color: T.textSecondary, display: "flex", gap: 6 }}>
                      <span style={{ color }}>✓</span> {p}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

      </div>
    </div>
  );
}

// ─── LOGIN PAGE ────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || "Login failed");
        setLoading(false);
        return;
      }
      // Store session
      localStorage.setItem("dv_session", data.session);
      localStorage.setItem("dv_user", JSON.stringify(data.user));
      onLogin(data.session, data.user);
    } catch {
      setError("Network error — please try again");
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      height: "100vh", background: T.navy, flexDirection: "column", gap: 32,
    }}>
      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: `linear-gradient(135deg, ${T.amber}, ${T.amberDim})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 24, color: T.navy, fontWeight: 900,
        }}>D</div>
        <div>
          <div style={{ fontSize: 24, fontFamily: T.sans, fontWeight: 700, color: T.textPrimary, letterSpacing: "-0.02em" }}>DentaVoice</div>
          <div style={{ fontSize: 12, fontFamily: T.mono, color: T.amber, letterSpacing: "0.08em" }}>DSO PLATFORM</div>
        </div>
      </div>

      {/* Login card */}
      <div style={{
        width: 380, background: T.navyMid,
        border: `1px solid ${T.navyBorder}`,
        borderRadius: 12, padding: 32,
        boxShadow: `0 0 40px rgba(0,0,0,0.4)`,
      }}>
        <div style={{ fontSize: 18, fontFamily: T.sans, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>Sign in</div>
        <div style={{ fontSize: 12, fontFamily: T.mono, color: T.textMuted, marginBottom: 24 }}>DentaVoice Operations Platform</div>

        <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 6, letterSpacing: "0.08em" }}>EMAIL</div>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com" required autoFocus
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 6, boxSizing: "border-box",
                background: T.navyLight, border: `1px solid ${T.navyBorder}`,
                color: T.textPrimary, fontSize: 13, fontFamily: T.sans, outline: "none",
              }}
              onFocus={e => e.target.style.borderColor = T.amber}
              onBlur={e => e.target.style.borderColor = T.navyBorder}
            />
          </div>

          <div>
            <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, marginBottom: 6, letterSpacing: "0.08em" }}>PASSWORD</div>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••••••" required
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 6, boxSizing: "border-box",
                background: T.navyLight, border: `1px solid ${T.navyBorder}`,
                color: T.textPrimary, fontSize: 13, fontFamily: T.sans, outline: "none",
              }}
              onFocus={e => e.target.style.borderColor = T.amber}
              onBlur={e => e.target.style.borderColor = T.navyBorder}
            />
          </div>

          {error && (
            <div style={{
              padding: "10px 14px", borderRadius: 6,
              background: T.red + "11", border: `1px solid ${T.red}44`,
              fontSize: 12, fontFamily: T.mono, color: T.red,
            }}>{error}</div>
          )}

          <button type="submit" disabled={loading} style={{
            padding: "12px 0", borderRadius: 6, border: "none", cursor: loading ? "default" : "pointer",
            background: loading ? T.navyBorder : T.amber,
            color: loading ? T.textMuted : T.navy,
            fontSize: 13, fontFamily: T.mono, fontWeight: 700,
            marginTop: 4, transition: "all 0.2s",
          }}>
            {loading ? "Signing in…" : "Sign in →"}
          </button>
        </form>
      </div>

      <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textDim }}>
        HIPAA · SOC 2 · AZURE · © 2025 DentaVoice
      </div>
    </div>
  );
}

// ─── ROOT APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage]           = useState("dashboard");
  const [callCount, setCallCount] = useState(0);
  const [session, setSession]     = useState(() => localStorage.getItem("dv_session") || null);
  const [user, setUser]           = useState(() => {
    try { return JSON.parse(localStorage.getItem("dv_user")); } catch { return null; }
  });
  const [authChecked, setAuthChecked] = useState(false);

  // Verify session is still valid on load
  useEffect(() => {
    if (!session) { setAuthChecked(true); return; }
    fetch(`${API_BASE}/auth/me`, {
      headers: { "X-Session": session }
    })
    .then(r => {
      if (!r.ok) throw new Error("Invalid session");
      return r.json();
    })
    .then(u => { setUser(u); setAuthChecked(true); })
    .catch(() => {
      localStorage.removeItem("dv_session");
      localStorage.removeItem("dv_user");
      setSession(null);
      setUser(null);
      setAuthChecked(true);
    });
  }, []);

  const handleLogin = (sess, usr) => {
    setSession(sess);
    setUser(usr);
  };

  const handleLogout = async () => {
    await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      headers: { "X-Session": session },
    }).catch(() => {});
    localStorage.removeItem("dv_session");
    localStorage.removeItem("dv_user");
    setSession(null);
    setUser(null);
  };

  // Add session to all API headers once logged in
  useEffect(() => {
    if (session) {
      HEADERS["X-Session"] = session;
    } else {
      delete HEADERS["X-Session"];
    }
  }, [session]);

  useEffect(() => {
    if (!session) return;
    const load = () =>
      fetch(`${API_BASE}/stats`, { headers: HEADERS })
        .then(r => r.json())
        .then(d => setCallCount(d.today?.total_calls ?? 0))
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [session]);

  // Show nothing while checking auth
  if (!authChecked) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: T.navy }}>
      <div style={{ fontFamily: T.mono, color: T.textMuted, fontSize: 13 }}>Loading…</div>
    </div>
  );

  // Show login if not authenticated
  if (!session || !user) return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${T.navy}; }
        input { color-scheme: dark; }
        input::placeholder { color: ${T.textDim}; }
      `}</style>
      <LoginPage onLogin={handleLogin} />
    </>
  );

  const pages = {
    dashboard:  <DashboardPage onNav={setPage} />,
    sites:      <SitesPage />,
    templates:  <TemplatesPage />,
    calls:      <CallLogsPage />,
    callbacks:  <CallbacksPage />,
    analytics:  <AnalyticsPage />,
    compliance: <CompliancePage />,
    billing:    <PlaceholderPage title="Billing" />,
    settings:   <SettingsPage />,
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&family=JetBrains+Mono:wght@400;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${T.navy}; color: ${T.textPrimary}; font-family: ${T.sans}; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${T.navy}; }
        ::-webkit-scrollbar-thumb { background: ${T.navyBorder}; border-radius: 3px; }
        @keyframes pulse-ring { 0% { opacity: 0.8; transform: scale(1); } 100% { opacity: 0; transform: scale(2.2); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input, textarea, select { color-scheme: dark; }
        input::placeholder, textarea::placeholder { color: ${T.textDim}; }
      `}</style>
      <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: T.navy }}>
        <Sidebar active={page} onNav={setPage} callCount={callCount} user={user} onLogout={handleLogout} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.navyMid }}>
          <div style={{ flex: 1, overflow: "auto" }}>
            {pages[page]}
          </div>
        </div>
      </div>
    </>
  );
}